import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Title, Tabs, Table, Metric, Button, Spinner, Message, Expander, useToast } from './bpm';
import { macroAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getPageIcon } from '../utils/pageIcons';
import './Macro.css';

const Macro = ({ onLogoClick = null }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { profile } = useAuth();
  const { showToast } = useToast();

  const canAccessMacroTab = useCallback((tabId) => {
    if (!profile) return true;
    if (profile.is_superadmin || profile.is_owner) return true;
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return true;
    return allowed.includes(tabId);
  }, [profile?.allowed_tabs, profile?.is_superadmin, profile?.is_owner]);
  
  // États pour les indices
  const [indices, setIndices] = useState([]);
  const [loadingIndices, setLoadingIndices] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  
  // États pour les matières premières
  const [commodities, setCommodities] = useState([]);
  const [loadingCommodities, setLoadingCommodities] = useState(false);
  const [selectedCommodity, setSelectedCommodity] = useState(null);
  
  // Charger les indices
  const loadIndices = useCallback(async () => {
    setLoadingIndices(true);
    try {
      const response = await macroAPI.getIndices();
      setIndices(response.data || []);
    } catch (err) {
      console.error('❌ [Macro] Erreur lors du chargement des indices:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      console.error('📊 [Macro] Détails de l\'erreur:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      });
      
      // Message d'erreur plus spécifique selon le type d'erreur
      let userMessage = errorMessage;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        userMessage = 'Le serveur ne répond pas dans les temps. Vérifiez que le backend est démarré sur http://localhost:8000';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        userMessage = 'Impossible de se connecter au serveur. Vérifiez que le backend est démarré.';
      }
      
      showToast(userMessage, 'error', 3000, 'Erreur de chargement des indices', 'Macro', getPageIcon('Macro'));
      setIndices([]);
    } finally {
      setLoadingIndices(false);
    }
  }, [showToast]);
  
  // Charger les matières premières
  const loadCommodities = useCallback(async () => {
    setLoadingCommodities(true);
    try {
      const response = await macroAPI.getCommodities();
      setCommodities(response.data || []);
    } catch (err) {
      console.error('❌ [Macro] Erreur lors du chargement des matières premières:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur inconnue';
      console.error('📊 [Macro] Détails de l\'erreur:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      });
      
      // Message d'erreur plus spécifique selon le type d'erreur
      let userMessage = errorMessage;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        userMessage = 'Le serveur ne répond pas dans les temps. Vérifiez que le backend est démarré sur http://localhost:8000';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        userMessage = 'Impossible de se connecter au serveur. Vérifiez que le backend est démarré.';
      }
      
      showToast(userMessage, 'error', 3000, 'Erreur de chargement des matières premières', 'Macro', getPageIcon('Macro'));
      setCommodities([]);
    } finally {
      setLoadingCommodities(false);
    }
  }, [showToast]);
  
  // Charger les données au montage et quand on change d'onglet
  useEffect(() => {
    if (activeTab === 0) {
      loadIndices();
    } else if (activeTab === 1) {
      loadCommodities();
    }
  }, [activeTab, loadIndices, loadCommodities]);
  
  // Colonnes pour le tableau des indices
  const indicesColumns = useMemo(() => [
    { key: 'nom', label: 'Indice', align: 'left' },
    { key: 'ticker', label: 'Ticker', align: 'left' },
    { key: 'prix_actuel', label: 'Prix Actuel', align: 'right' },
    { key: 'variation_pct', label: 'Variation', align: 'right' },
    { key: 'h52_high', label: 'H52 High', align: 'right' },
    { key: 'h52_low', label: 'H52 Low', align: 'right' },
    { key: 'volatilite_30d', label: 'Volatilité (30j)', align: 'right' },
    { key: 'ytd_return', label: 'YTD', align: 'right' },
    { key: 'distance_ath', label: 'Distance ATH', align: 'right' },
  ], []);
  
  // Données formatées pour le tableau des indices
  const indicesTableData = useMemo(() => {
    return indices.map(index => ({
      ...index,
      prix_actuel: index.prix_actuel?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A',
      variation_pct: `${index.variation_pct >= 0 ? '+' : ''}${index.variation_pct?.toFixed(2)}%` || 'N/A',
      h52_high: index.h52_high?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A',
      h52_low: index.h52_low?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A',
      volatilite_30d: `${index.volatilite_30d?.toFixed(2)}%` || 'N/A',
      ytd_return: `${index.ytd_return >= 0 ? '+' : ''}${index.ytd_return?.toFixed(2)}%` || 'N/A',
      distance_ath: `${Math.abs(index.distance_ath || 0).toFixed(2)}%` || 'N/A',
    }));
  }, [indices]);
  
  // Colonnes pour le tableau des matières premières
  const commoditiesColumns = useMemo(() => [
    { key: 'nom', label: 'Matière Première', align: 'left' },
    { key: 'categorie', label: 'Catégorie', align: 'left' },
    { key: 'ticker', label: 'Ticker', align: 'left' },
    { key: 'prix_actuel', label: 'Prix Actuel', align: 'right' },
    { key: 'variation_pct', label: 'Variation', align: 'right' },
    { key: 'h52_high', label: 'H52 High', align: 'right' },
    { key: 'h52_low', label: 'H52 Low', align: 'right' },
    { key: 'volatilite_30d', label: 'Volatilité (30j)', align: 'right' },
    { key: 'ytd_return', label: 'YTD', align: 'right' },
    { key: 'distance_ath', label: 'Distance ATH', align: 'right' },
  ], []);
  
  // Données formatées pour le tableau des matières premières
  const commoditiesTableData = useMemo(() => {
    return commodities.map(commodity => ({
      ...commodity,
      prix_actuel: commodity.prix_actuel?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A',
      variation_pct: `${commodity.variation_pct >= 0 ? '+' : ''}${commodity.variation_pct?.toFixed(2)}%` || 'N/A',
      h52_high: commodity.h52_high?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A',
      h52_low: commodity.h52_low?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A',
      volatilite_30d: `${commodity.volatilite_30d?.toFixed(2)}%` || 'N/A',
      ytd_return: `${commodity.ytd_return >= 0 ? '+' : ''}${commodity.ytd_return?.toFixed(2)}%` || 'N/A',
      distance_ath: `${Math.abs(commodity.distance_ath || 0).toFixed(2)}%` || 'N/A',
    }));
  }, [commodities]);
  
  // Gestion de la sélection d'un indice
  const handleIndexSelect = useCallback((row) => {
    const index = indices.find(idx => idx.ticker === row.ticker);
    setSelectedIndex(index || null);
  }, [indices]);
  
  // Gestion de la sélection d'une matière première
  const handleCommoditySelect = useCallback((row) => {
    const commodity = commodities.find(com => com.ticker === row.ticker);
    setSelectedCommodity(commodity || null);
  }, [commodities]);

  const allTabs = [
    {
      tabId: 'macro-indices',
      label: 'Indices',
      content: (
        <div className="macro-tab-content">
          {loadingIndices ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem 0', marginTop: '-20px' }}>
              <Spinner text="Chargement des indices..." size="medium" />
            </div>
          ) : indices.length === 0 ? (
            <Message type="warning">
              Aucune donnée d'indice disponible. Vérifiez votre connexion internet.
            </Message>
          ) : (
            <>
              <Table
                columns={indicesColumns}
                data={indicesTableData}
                striped={true}
                hover={true}
                onRowClick={handleIndexSelect}
                defaultSortColumn="nom"
                defaultSortDirection="asc"
              />
              
              {selectedIndex && (
                <div className="macro-details-section">
                  <div className="macro-details-header">
                    <Title level={3}>{selectedIndex.nom} ({selectedIndex.ticker})</Title>
                  </div>
                  
                  <div className="macro-metrics-grid">
                    <Metric
                      label="Cours Actuel"
                      value={selectedIndex.prix_actuel?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                      delta={selectedIndex.variation_pct}
                      deltaType={selectedIndex.variation_pct >= 0 ? 'normal' : 'inverse'}
                      deltaDecimals={2}
                      currency=""
                    />
                    
                    <Metric
                      label="Plus Haut 52 Sem"
                      value={selectedIndex.h52_high?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                    />
                    
                    <Metric
                      label="Plus Bas 52 Sem"
                      value={selectedIndex.h52_low?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                    />
                    
                    <Metric
                      label="Volatilité (30j)"
                      value={`${selectedIndex.volatilite_30d?.toFixed(2) || 0}%`}
                    />
                    
                    <Metric
                      label="Performance YTD"
                      value={`${selectedIndex.ytd_return >= 0 ? '+' : ''}${selectedIndex.ytd_return?.toFixed(2) || 0}%`}
                      deltaType={selectedIndex.ytd_return >= 0 ? 'normal' : 'inverse'}
                    />
                    
                    <Metric
                      label="Distance ATH"
                      value={`${Math.abs(selectedIndex.distance_ath || 0).toFixed(2)}%`}
                      delta={selectedIndex.distance_ath}
                      deltaType="inverse"
                      deltaDecimals={2}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )
    },
    {
      tabId: 'macro-matieres-premieres',
      label: 'Matières Premières',
      content: (
        <div className="macro-tab-content">
          {loadingCommodities ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '2rem 0', marginTop: '-20px' }}>
              <Spinner text="Chargement des matières premières..." size="medium" />
            </div>
          ) : commodities.length === 0 ? (
            <Message type="warning">
              Aucune donnée de matière première disponible. Vérifiez votre connexion internet.
            </Message>
          ) : (
            <>
              <Table
                columns={commoditiesColumns}
                data={commoditiesTableData}
                striped={true}
                hover={true}
                onRowClick={handleCommoditySelect}
                defaultSortColumn="nom"
                defaultSortDirection="asc"
              />
              
              {selectedCommodity && (
                <div className="macro-details-section">
                  <div className="macro-details-header">
                    <Title level={3}>{selectedCommodity.nom} ({selectedCommodity.ticker})</Title>
                  </div>
                  
                  <div className="macro-metrics-grid">
                    <Metric
                      label="Prix Actuel"
                      value={selectedCommodity.prix_actuel?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                      delta={selectedCommodity.variation_pct}
                      deltaType={selectedCommodity.variation_pct >= 0 ? 'normal' : 'inverse'}
                      deltaDecimals={2}
                      currency=""
                    />
                    
                    <Metric
                      label="Plus Haut 52 Sem"
                      value={selectedCommodity.h52_high?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                    />
                    
                    <Metric
                      label="Plus Bas 52 Sem"
                      value={selectedCommodity.h52_low?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                    />
                    
                    <Metric
                      label="Volatilité (30j)"
                      value={`${selectedCommodity.volatilite_30d?.toFixed(2) || 0}%`}
                    />
                    
                    <Metric
                      label="Performance YTD"
                      value={`${selectedCommodity.ytd_return >= 0 ? '+' : ''}${selectedCommodity.ytd_return?.toFixed(2) || 0}%`}
                      deltaType={selectedCommodity.ytd_return >= 0 ? 'normal' : 'inverse'}
                    />
                    
                    <Metric
                      label="Distance ATH"
                      value={`${Math.abs(selectedCommodity.distance_ath || 0).toFixed(2)}%`}
                      delta={selectedCommodity.distance_ath}
                      deltaType="inverse"
                      deltaDecimals={2}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )
    },
    {
      tabId: 'macro-macro',
      label: 'Macro',
      content: (
        <div className="macro-tab-content">
          <Title level={2}>Macro</Title>
          
          <div className="macro-info-grid">
            <div className="macro-info-column">
              <Title level={3}>Données monétaires EUR</Title>
              <ul className="macro-info-list">
                <li>Masse monétaire M2</li>
                <li>Taux BCE (refinancement)</li>
                <li>Inflation MoM/YoY</li>
                <li>PIB trimestriel</li>
                <li>Chômage (Eurostat)</li>
              </ul>
              
              <Title level={3} style={{ marginTop: '2rem' }}>Sources prévues</Title>
              <ul className="macro-info-list">
                <li>BCE (Banque Centrale Européenne)</li>
                <li>Eurostat</li>
                <li>FRED (Federal Reserve Economic Data)</li>
              </ul>
            </div>
            
            <div className="macro-info-column">
              <Title level={3}>Données monétaires USD</Title>
              <ul className="macro-info-list">
                <li>Masse monétaire M2</li>
                <li>Fed Funds Rate</li>
                <li>CPI/Core CPI</li>
                <li>GDP Growth</li>
                <li>Unemployment Rate</li>
              </ul>
              
              <Title level={3} style={{ marginTop: '2rem' }}>API prévues</Title>
              <ul className="macro-info-list">
                <li>Federal Reserve FRED API</li>
                <li>Yahoo Finance</li>
                <li>Alpha Vantage</li>
              </ul>
            </div>
          </div>
          
          <Message type="warning" style={{ marginTop: '2rem' }}>
            Configuration requise : Clés API pour FRED, Alpha Vantage, etc.
          </Message>
        </div>
      )
    },
    {
      tabId: 'macro-historique-120-ans',
      label: 'Historique 120 ans',
      content: (
        <div className="macro-tab-content">
          <Title level={2}>Perspective Historique 120 ans</Title>
          
          <Message type="info" style={{ marginBottom: '2rem' }}>
            Objectif : Mettre en perspective les données actuelles dans un contexte historique long terme
          </Message>
          
          <div className="macro-info-grid">
            <div className="macro-info-column">
              <Title level={3}>Données historiques prévues</Title>
              <ul className="macro-info-list">
                <li>Indices boursiers depuis 1900</li>
                <li>Prix de l'or depuis 1900</li>
                <li>Inflation USA/Europe 120 ans</li>
                <li>Taux d'intérêt long terme</li>
                <li>Ratios P/E historiques</li>
              </ul>
            </div>
            
            <div className="macro-info-column">
              <Title level={3}>Analyses prévues</Title>
              <ul className="macro-info-list">
                <li>Cycles économiques longs</li>
                <li>Krach historiques vs actuels</li>
                <li>Corrélations inter-actifs</li>
                <li>Valorisations relatives</li>
                <li>Signaux d'alerte historiques</li>
              </ul>
            </div>
          </div>
          
          <div style={{ marginTop: '2rem' }}>
            <Expander title="Aperçu : Evolution du P/E S&P 500 depuis 1900">
              <div style={{ padding: '1rem 0' }}>
                <Message type="info">
                  Les données historiques seront intégrées prochainement. Cette fonctionnalité permettra de visualiser
                  l'évolution des ratios P/E du S&P 500 sur plus de 120 ans, avec des références aux périodes clés
                  (Grande Dépression, Bulle dot-com, etc.).
                </Message>
              </div>
            </Expander>
          </div>
        </div>
      )
    }
  ];

  const tabs = allTabs.filter(tab => canAccessMacroTab(tab.tabId));

  useEffect(() => {
    if (activeTab >= tabs.length && tabs.length > 0) {
      setActiveTab(0);
    }
  }, [activeTab, tabs.length]);

  return (
    <div className="macro-page">
      <Tabs 
        tabs={tabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
};

export default Macro;
